<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  ArrowLeftRight,
  BookOpen,
  Boxes,
  ChartColumn,
  ClipboardList,
  Clock,
  Gauge,
  Info,
  LifeBuoy,
  UserCog,
} from 'lucide-vue-next'
import { findHelpArticle, helpArticles, type HelpArticle } from '../helpArticles'

const route = useRoute()
const { t } = useI18n()

const slug = computed(() => String(route.params.slug ?? ''))
const article = computed<HelpArticle | null>(() => (slug.value ? findHelpArticle(slug.value) ?? null : null))

const iconMap = {
  book: BookOpen,
  gauge: Gauge,
  clipboard: ClipboardList,
  boxes: Boxes,
  user: UserCog,
  chart: ChartColumn,
  clock: Clock,
  handover: ArrowLeftRight,
  info: Info,
  lifebuoy: LifeBuoy,
} as const

function articleIcon(name: string) {
  return iconMap[name as keyof typeof iconMap] ?? Info
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('shopfloor.help.eyebrow') }}</p>
      <h2>{{ t('shopfloor.help.title') }}</h2>
      <p>{{ t('shopfloor.help.subtitle') }}</p>
    </div>
  </div>

  <!-- Article list (index) -->
  <div v-if="!slug" class="help-grid">
    <RouterLink v-for="a in helpArticles" :key="a.slug" :to="`/help/${a.slug}`" class="help-card">
      <span class="help-card__icon"><component :is="articleIcon(a.icon)" :size="22" aria-hidden="true" /></span>
      <div class="help-card__body">
        <h3>{{ a.title }}</h3>
        <p>{{ a.description }}</p>
      </div>
    </RouterLink>
  </div>

  <!-- Article detail -->
  <template v-else-if="article">
    <RouterLink class="back" to="/help">← {{ t('shopfloor.help.back') }}</RouterLink>
    <article class="help-article">
      <div class="help-article__head">
        <span class="help-card__icon help-article__icon">
          <component :is="articleIcon(article.icon)" :size="26" aria-hidden="true" />
        </span>
        <div>
          <h2>{{ article.title }}</h2>
          <p>{{ article.description }}</p>
        </div>
      </div>

      <section v-for="(section, index) in article.sections" :key="index" class="help-section">
        <h3>{{ section.heading }}</h3>
        <ul>
          <li v-for="(line, i) in section.body" :key="i">{{ line }}</li>
        </ul>
      </section>

      <section v-if="article.tips && article.tips.length" class="help-tips">
        <h3>{{ t('shopfloor.help.tipsTitle') }}</h3>
        <ul>
          <li v-for="(tip, i) in article.tips" :key="i">{{ tip }}</li>
        </ul>
      </section>
    </article>
  </template>

  <!-- Not found -->
  <div v-else class="state-message">
    <LifeBuoy :size="36" aria-hidden="true" />
    <p>{{ t('shopfloor.help.notFound') }}</p>
    <RouterLink class="btn-primary" to="/help">{{ t('shopfloor.help.back') }}</RouterLink>
  </div>
</template>

<style scoped>
.page-heading {
  margin-bottom: 20px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }
.back {
  display: inline-block;
  margin-bottom: 16px;
  color: #27705d;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}
.back:hover { text-decoration: underline; }

.help-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
}
.help-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 18px;
  border: 1px solid #e2e6e3;
  border-radius: 12px;
  background: #fff;
  text-decoration: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.help-card:hover {
  border-color: #b8cfc6;
  box-shadow: 0 6px 18px rgb(26 55 91 / 8%);
}
.help-card__icon {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  color: #1f5c4b;
  background: #eaf0ed;
}
.help-card__body h3 {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #1a2b26;
}
.help-card__body p {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #65736e;
}

.help-article {
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 12px;
  padding: 24px;
}
.help-article__head {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f2f1;
}
.help-article__icon {
  width: 52px;
  height: 52px;
}
.help-article__head h2 { margin: 0 0 4px; font-size: 20px; }
.help-article__head p { margin: 0; color: #5f6b6a; font-size: 14px; }
.help-section { margin-bottom: 18px; }
.help-section h3 {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1a2b26;
}
.help-section ul, .help-tips ul {
  margin: 0;
  padding-left: 20px;
  display: grid;
  gap: 6px;
}
.help-section li, .help-tips li {
  font-size: 14px;
  line-height: 1.5;
  color: #3c4b46;
}
.help-tips {
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #fffdf5;
  border: 1px solid #f0e3b8;
}
.help-tips h3 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: #856404;
}
.help-tips li { color: #6d5a14; }

.state-message {
  padding: 48px 0;
  text-align: center;
  color: #5f6b6a;
  display: grid;
  justify-items: center;
  gap: 12px;
}
.state-message p { margin: 0; }

.btn-primary {
  display: inline-block;
  padding: 10px 22px;
  background: #27705d;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}
.btn-primary:hover { background: #1f5c4b; }

@media (max-width: 900px) {
  .help-grid { grid-template-columns: 1fr; }
  .help-article { padding: 16px; }
}
</style>
